import { formatAmount, formatCurrencyInput } from '@/lib/utils/formatters';
import { getPaymentLogDetail } from '@/lib/api/paymentLogService';
import { getConfiguredRefundFeePercent } from '@/lib/utils/paymentCalculations';
import { computePaymentLogBaseFee } from '@/features/application/utils/signaturePaymentHelpers';
import type {
  AdjustmentType,
  PaymentAdjustmentApplicationContext,
  PaymentAdjustmentHistoryLine,
  PaymentAdjustmentSubmitPayload,
  PaymentCalculation,
} from '../../types/modals';

export {
  ACCESS_FEE_REASONS,
  APPLICATION_FEE_INVOICE_REASONS,
  CHARGEBACK_CONFIRM_MESSAGE,
  CONTRACT_INVOICE_REASONS,
} from '../../constants';

export function toNumber(value: unknown) {
  const n = Number(String(value ?? 0).replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function isValidAmountInput(value: string) {
  const normalized = value.replace(/[$,\s]/g, '');
  if (
    !normalized ||
    normalized === '-' ||
    normalized === '.' ||
    normalized === '-.'
  ) {
    return false;
  }
  return /^-?\d+(\.\d+)?$/.test(normalized);
}

export function amountString(value: unknown) {
  return String(value ?? '').replace(/[$,\s]/g, '');
}

export function positiveAmountString(value: unknown) {
  return amountString(value).replace(/^-/, '');
}

export function formatCaseDate(value: unknown) {
  if (value == null || value === '') return undefined;
  const raw = String(value);
  const timestamp = Number(raw);
  const date =
    Number.isFinite(timestamp) && raw.length <= 10
      ? new Date(timestamp * 1000)
      : new Date(raw);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
}

export function computeIsContractPayment(paymentType: string) {
  return (
    paymentType.includes('CONTRACT') || paymentType.includes('BALANCE_ADJUSTMENT')
  );
}

/** Angular `isadjustmentPayment` — detail comes from `/api/payment_adjustment/:paId`. */
export function isAdjustmentPaymentLine(
  payment: PaymentAdjustmentHistoryLine | null,
) {
  const paymentType = String(payment?.payment_type ?? '');
  if (!paymentType || payment?.payment_source) return false;
  return [
    'CONTRACT_CHARGEBACK',
    'CHGBACK_APP_FEE',
    'CHGBACK_CONTRACT',
    'CONTRACT_INVOICE',
    'APPLICATION_FEE_INVOICE',
    'BALANCE_ADJUSTMENT',
    'DEFAULT_INVOICE',
  ].includes(paymentType);
}

/** Angular `loadLogDetails()` — card line uses `paymentDetail.card_number` only. */
export async function fetchPaymentLogCardNumber(
  payment: PaymentAdjustmentHistoryLine | null,
): Promise<string> {
  if (!payment) return '';

  const paId = payment.pa_id;
  const logId = payment.payment_log_id;

  const detail = isAdjustmentPaymentLine(payment)
    ? paId != null && paId !== ''
      ? await getPaymentLogDetail(paId, 'payment_adjustment')
      : null
    : logId != null && logId !== ''
      ? await getPaymentLogDetail(logId, 'payment_log')
      : null;

  return detail?.card_number ? String(detail.card_number).trim() : '';
}

export function computeStartBalance(
  payment: PaymentAdjustmentHistoryLine | null,
  application: PaymentAdjustmentApplicationContext | null,
  isContractPayment: boolean,
) {
  if (!payment) return 0;
  // Angular Default Invoice: starting balance is 0 (nothing paid toward the default yet),
  // not the applicant's application-fee total.
  const paymentType = String(payment?.payment_type ?? payment?.type ?? '');
  if (paymentType.includes('DEFAULT')) {
    return 0;
  }
  const history = Array.isArray(application?.payment_history)
    ? application.payment_history
    : [];
  if (isContractPayment) {
    const total = history.reduce((sum: number, entry) => {
      const line = entry as PaymentAdjustmentHistoryLine;
      const linePaymentType = String(line?.payment_type ?? line?.type ?? '');
      if (
        !linePaymentType.includes('CONTRACT') ||
        line?.payment_status !== 'Completed'
      ) {
        return sum;
      }
      return sum + computePaymentLogBaseFee(line);
    }, 0);
    return total || computePaymentLogBaseFee(payment);
  }

  const applicantId = String(payment?.fk_applicant_id ?? '');
  const total = history.reduce((sum: number, entry) => {
    const line = entry as PaymentAdjustmentHistoryLine;
    const linePaymentType = String(line?.payment_type ?? line?.type ?? '');
    if (
      linePaymentType.includes('APPLICATION_FEE') &&
      String(line?.fk_applicant_id ?? '') === applicantId &&
      line?.payment_status === 'Completed'
    ) {
      return sum + toNumber(line.payment_amount);
    }
    return sum;
  }, 0);
  return total || toNumber(payment.payment_amount);
}

export function getApplicantStatus(
  payment: PaymentAdjustmentHistoryLine | null,
  application: PaymentAdjustmentApplicationContext | null,
) {
  const applicantId = String(payment?.fk_applicant_id ?? '');
  const coResidents = application?.co_resident_application;
  if (applicantId && coResidents) {
    if (
      !Array.isArray(coResidents) &&
      coResidents[applicantId]?.applicant_status
    ) {
      return String(coResidents[applicantId].applicant_status);
    }
    if (Array.isArray(coResidents)) {
      const match = coResidents.find(
        (resident: { applicant_id?: string | number }) =>
          String(resident?.applicant_id ?? '') === applicantId,
      );
      if (match?.applicant_status) return String(match.applicant_status);
    }
  }
  return String(
    payment?.applicant_status ?? application?.applicant_status ?? '',
  );
}

export function getApplicantUser(
  payment: PaymentAdjustmentHistoryLine | null,
  application: PaymentAdjustmentApplicationContext | null,
) {
  const applicantId = String(payment?.fk_applicant_id ?? '');
  const coResidents = application?.co_resident_application;
  if (applicantId && coResidents) {
    if (!Array.isArray(coResidents) && coResidents[applicantId]?.user_data) {
      return coResidents[applicantId].user_data as Record<string, unknown>;
    }
    if (Array.isArray(coResidents)) {
      const match = coResidents.find(
        (resident: { applicant_id?: string | number }) =>
          String(resident?.applicant_id ?? '') === applicantId,
      );
      if (match?.user_data)
        return match.user_data as Record<string, unknown>;
    }
  }
  return (application?.user_data ?? {}) as Record<string, unknown>;
}

/** Angular payment-refund calculator: contract adjustments are editable via edit icon. */
export function isContractAdjustmentAmountEditable(
  adjustmentType: AdjustmentType,
  balanceAdjustmentAction: string,
): boolean {
  if (!adjustmentType || adjustmentType === 'chargeback') {
    return false;
  }
  if (adjustmentType === 'balance_adjustment') {
    return balanceAdjustmentAction === 'ADJUST';
  }
  return (
    adjustmentType === 'invoice' ||
    adjustmentType === 'refund_partial' ||
    adjustmentType === 'refund_full' ||
    adjustmentType === 'default_invoice'
  );
}

export function computeDefaultAdjustmentAmount(params: {
  adjustmentType: AdjustmentType;
  isContractPayment: boolean;
  payment: PaymentAdjustmentHistoryLine | null;
  accessFee: number;
  startBalance: number;
  adjustmentReason: string;
  balanceAdjustmentAction: string;
  balanceDue: number;
  chargeRefundFee?: boolean;
  refundProcessingFee?: boolean;
  paymentConfig?: Record<string, unknown> | null;
}) {
  const {
    adjustmentType,
    isContractPayment,
    payment,
    accessFee,
    startBalance,
    adjustmentReason,
    balanceAdjustmentAction,
    balanceDue,
    chargeRefundFee = false,
    refundProcessingFee = false,
    paymentConfig = null,
  } = params;
  if (!adjustmentType) return 0;
  if (adjustmentType === 'chargeback') {
    return isContractPayment ? 0 : toNumber(payment?.payment_amount);
  }
  if (!isContractPayment) return toNumber(payment?.payment_amount);
  if (adjustmentType === 'invoice') return accessFee - startBalance;
  if (adjustmentType === 'refund_full') {
    return computeContractFullRefundAmounts({
      startBalance,
      chargeRefundFee,
      refundProcessingFee,
      paymentSource: String(payment?.payment_source ?? ''),
      paymentConfig,
      paymentLogSurchargeFee: toNumber(
        payment?.payment_log_surcharge_fee ?? payment?.surcharge_fee,
      ),
    }).adjustmentPayment;
  }
  if (adjustmentType === 'refund_partial')
    return adjustmentReason === 'RENT_DECREASED' ||
      adjustmentReason === 'OVERPAID'
      ? startBalance - accessFee
      : 0;
  if (adjustmentType === 'balance_adjustment') {
    if (balanceAdjustmentAction === 'ADJUST') {
      return (accessFee - startBalance) * -1;
    }
    return balanceDue;
  }
  return 0;
}

/**
* Angular `paymentCalculate('contract_full')` with Charge Refund Fee /
* Refund Processing Fee checkboxes.
*/
export function computeContractFullRefundAmounts(params: {
  startBalance: number;
  chargeRefundFee: boolean;
  refundProcessingFee: boolean;
  paymentSource: string;
  paymentConfig: Record<string, unknown> | null | undefined;
  paymentLogSurchargeFee: number;
}): {
  adjustmentPayment: number;
  refundFee: number;
  refundProcessingFeeAmount: number;
  refundAmount: number;
  totalBalance: number;
} {
  const {
    startBalance,
    chargeRefundFee,
    refundProcessingFee,
    paymentSource,
    paymentConfig,
    paymentLogSurchargeFee,
  } = params;

  let adjustmentPayment = startBalance;
  let refundFee = 0;
  let refundProcessingFeeAmount = 0;

  if (chargeRefundFee && paymentSource) {
    const refundFeePercent = getConfiguredRefundFeePercent(
      paymentConfig,
      paymentSource,
    );
    if (refundFeePercent) {
      refundFee = 0.01 * refundFeePercent * startBalance;
      adjustmentPayment = startBalance - refundFee;
    }
  }

  if (refundProcessingFee && paymentLogSurchargeFee) {
    refundProcessingFeeAmount = paymentLogSurchargeFee;
    adjustmentPayment += paymentLogSurchargeFee;
  }

  return {
    adjustmentPayment,
    refundFee,
    refundProcessingFeeAmount,
    refundAmount: adjustmentPayment,
    // Angular forces Account Balance to $0 after full-contract refund calc.
    totalBalance: 0,
  };
}

export function computePaymentCalculation(params: {
  adjustmentType: AdjustmentType;
  adjustmentAmount: string;
  defaultAdjustmentAmount: number;
  startBalance: number;
  balanceDue: number;
  balanceAdjustmentAction: string;
  chargeRefundFee?: boolean;
  refundProcessingFee?: boolean;
  paymentSource?: string;
  paymentConfig?: Record<string, unknown> | null;
  paymentLogSurchargeFee?: number;
}): PaymentCalculation {
  const {
    adjustmentType,
    adjustmentAmount,
    defaultAdjustmentAmount,
    startBalance,
    balanceDue,
    balanceAdjustmentAction,
    chargeRefundFee = false,
    refundProcessingFee = false,
    paymentSource = '',
    paymentConfig = null,
    paymentLogSurchargeFee = 0,
  } = params;

  if (adjustmentType === 'refund_full') {
    const fullRefund = computeContractFullRefundAmounts({
      startBalance,
      chargeRefundFee,
      refundProcessingFee,
      paymentSource,
      paymentConfig,
      paymentLogSurchargeFee,
    });
    const useFeeAdjustedDefault =
      !adjustmentAmount ||
      Math.abs(toNumber(adjustmentAmount) - defaultAdjustmentAmount) < 0.005;
    const adjustmentPayment = useFeeAdjustedDefault
      ? fullRefund.adjustmentPayment
      : toNumber(adjustmentAmount);

    return {
      totalBalance: useFeeAdjustedDefault
        ? fullRefund.totalBalance
        : startBalance - adjustmentPayment,
      startBalance,
      adjustmentPayment,
      balanceOwnPayment: 0,
      balanaceOwnPayment: 0,
      refundAmount: adjustmentPayment,
      refundFee: fullRefund.refundFee,
      refundProcessingFeeAmount: fullRefund.refundProcessingFeeAmount,
    };
  }

  const rawAdjustmentPayment = adjustmentAmount
    ? toNumber(adjustmentAmount)
    : adjustmentType
      ? defaultAdjustmentAmount
      : 0;
  const adjustmentPayment =
    adjustmentType === 'balance_adjustment' &&
    balanceAdjustmentAction !== 'ADJUST'
      ? rawAdjustmentPayment * -1
      : rawAdjustmentPayment;
  // 'refund_full' is handled by the early return above, so it never reaches here.
  const isRefund =
    adjustmentType === 'refund' || adjustmentType === 'refund_partial';
  let refundAmount = 0;
  let totalBalance = adjustmentType ? startBalance : 0;
  let balanceOwnPayment = adjustmentPayment;

  if (isRefund) {
    refundAmount = adjustmentPayment;
    totalBalance = startBalance - adjustmentPayment;
    balanceOwnPayment = 0;
  } else if (adjustmentType === 'balance_adjustment') {
    if (balanceAdjustmentAction === 'ADJUST') {
      totalBalance = startBalance + balanceDue + adjustmentPayment;
      balanceOwnPayment = balanceDue + adjustmentPayment;
    } else {
      totalBalance = startBalance;
      balanceOwnPayment = 0;
    }
  } else if (adjustmentType === 'default_invoice') {
    // Angular Default Invoice (updateBalance): the entered amount is the balance owed.
    totalBalance = startBalance;
    balanceOwnPayment = adjustmentPayment;
    refundAmount = 0;
  }

  return {
    totalBalance,
    startBalance,
    adjustmentPayment,
    balanceOwnPayment,
    balanaceOwnPayment: balanceOwnPayment,
    refundAmount,
    refundFee: 0,
    refundProcessingFeeAmount: 0,
  };
}

/**
 * Angular `refundProcess()` — original CONTRACT charges that have no completed
 * refund child (`fk_payment_log_id` points at the charge).
 */
export function getUnrefundedContractPayments(
  history: unknown[] | undefined | null,
): PaymentAdjustmentHistoryLine[] {
  const paymentHistory = Array.isArray(history) ? history : [];
  return paymentHistory.filter((entry) => {
    const line = entry as PaymentAdjustmentHistoryLine;
    const lineType = String(line?.payment_type ?? line?.type ?? '');
    if (!lineType.includes('CONTRACT') || line?.fk_payment_log_id) {
      return false;
    }
    const logId = line?.payment_log_id;
    if (logId == null || logId === '') return false;
    const hasRefund = paymentHistory.some((childEntry) => {
      const child = childEntry as PaymentAdjustmentHistoryLine;
      return (
        String(child?.fk_payment_log_id) === String(logId) &&
        child?.payment_status === 'Completed'
      );
    });
    return !hasRefund;
  }) as PaymentAdjustmentHistoryLine[];
}

function formatHistoryCardNumber(line: PaymentAdjustmentHistoryLine) {
  const cardNo = String(line.card_no ?? '').trim();
  if (cardNo) return `XXXXXXXXXXXX${cardNo}`;
  const cardNumber = String(line.card_number ?? '').trim();
  return cardNumber;
}

/**
 * Angular multi-card full-refund confirm: one Action/For/Method/Card/Amount
 * block per unrefunded CONTRACT payment, then Total Refund Amount.
 */
export function buildMultiContractRefundConfirmationMessage(params: {
  unrefundedPayments: PaymentAdjustmentHistoryLine[];
  chargeRefundFee: boolean;
  paymentConfig?: Record<string, unknown> | null;
  totalRefundAmount: string | number | null | undefined;
}) {
  const {
    unrefundedPayments,
    chargeRefundFee,
    paymentConfig = null,
    totalRefundAmount,
  } = params;

  const applySurcharge = unrefundedPayments.reduce(
    (sum, line) => sum + toNumber(line.payment_log_surcharge_fee),
    0,
  );

  const lines = ['Do you want to Refund Payment?'];

  unrefundedPayments.forEach((line, index) => {
    const paidBy = String(line.paid_by ?? line.payer_name ?? '');
    const paymentAmount = toNumber(line.payment_amount);
    const baseAmount = index === 0 ? paymentAmount - applySurcharge : paymentAmount;

    let refundFee = 0;
    if (chargeRefundFee && line.payment_source) {
      const refundFeePercent = getConfiguredRefundFeePercent(
        paymentConfig,
        String(line.payment_source),
      );
      if (refundFeePercent) {
        refundFee = 0.01 * refundFeePercent * baseAmount;
      }
    }

    lines.push('');
    lines.push(`Action: Refund and send a notice to: ${paidBy}`);
    lines.push(`For: ${String(line.payment_type ?? line.type ?? '')}`);
    lines.push(`Payment Method: ${String(line.payment_source ?? '')}`);
    const card = formatHistoryCardNumber(line);
    if (card) lines.push(`Card: ${card}`);
    lines.push(`Refund Amount: ${formatAmount(baseAmount - refundFee)}`);
  });

  lines.push('');
  lines.push(`Total Refund Amount: ${formatAmount(totalRefundAmount)}`);
  return lines.join('\n');
}

export function buildRefundConfirmationMessage(params: {
  payment: PaymentAdjustmentHistoryLine | null;
  applicantUser: Record<string, unknown>;
  paymentType: string;
  adjustmentType: AdjustmentType;
  calculation: PaymentCalculation;
  /** Angular `paymentDetail.card_number` from GET payment log/adjustment detail. */
  paymentLogCardNumber?: string;
  application?: PaymentAdjustmentApplicationContext | null;
  chargeRefundFee?: boolean;
  paymentConfig?: Record<string, unknown> | null;
}) {
  const {
    payment,
    applicantUser,
    paymentType,
    adjustmentType,
    calculation,
    paymentLogCardNumber,
    application = null,
    chargeRefundFee = false,
    paymentConfig = null,
  } = params;

  const paymentSource = String(payment?.payment_source ?? '');
  const paymentStatus = String(payment?.payment_status ?? '');
  const unrefundedPayments = getUnrefundedContractPayments(
    application?.payment_history,
  );

  // Angular: multi-payment popup only for full contract refund on PAYPAL/CARDCONNECT
  // when more than one unrefunded original CONTRACT charge exists.
  if (
    adjustmentType === 'refund_full' &&
    ['PAYPAL', 'CARDCONNECT'].includes(paymentSource) &&
    unrefundedPayments.length > 1 &&
    !['AuthorizationPending', 'Rejected'].includes(paymentStatus)
  ) {
    return buildMultiContractRefundConfirmationMessage({
      unrefundedPayments,
      chargeRefundFee,
      paymentConfig,
      totalRefundAmount: calculation.refundAmount,
    });
  }

  const payerName =
    payment?.payer_name ||
    payment?.paid_by ||
    [applicantUser?.first_name, applicantUser?.last_name]
      .filter(Boolean)
      .join(' ');
  const email =
    payment?.payer_email || payment?.email || applicantUser?.email;
  const phone =
    payment?.payer_phone ||
    payment?.phone ||
    applicantUser?.mobile_no ||
    applicantUser?.phone;
  const cardNumber = paymentLogCardNumber?.trim() ?? '';
  const refundAmount =
    adjustmentType === 'refund'
      ? payment?.payment_amount
      : calculation.refundAmount;
  const lines = ['Do you want to Refund Payment?'];
  if (payerName)
    lines.push(`Action: Refund and send a notice to: ${payerName}`);
  if (email) lines.push(String(email));
  if (phone) lines.push(String(phone));
  lines.push(`For: ${paymentType || '-'}`);
  lines.push(`Payment Method: ${payment?.payment_source || '-'}`);
  lines.push(`Refund Amount: ${formatAmount(refundAmount)}`);
  if (cardNumber) lines.push(`Card: ${cardNumber}`);
  return lines.join('\n');
}

/** Angular external-refund branch in `refundProcess()`. */
export function buildExternalRefundConfirmationMessage(
  adjustmentSubtype: string,
) {
  return adjustmentSubtype === 'cancel_certificate'
    ? 'Do you want to External Refund and cancel the certificate.'
    : 'Do you want to External Refund . ?';
}

export function mapAdjustmentTypeToAngular(
  type: AdjustmentType,
  paymentType: string,
) {
  if (paymentType?.includes('APPLICATION_FEE')) {
    if (type === 'invoice') return 'application_fee_invoice';
    if (type === 'refund' || type === 'refund_partial' || type === 'refund_full')
      return 'refund_application';
    if (type === 'chargeback') return 'chgback_app_fee';
  }
  if (paymentType?.includes('CONTRACT')) {
    if (type === 'invoice') return 'contract_invoice';
    if (type === 'refund_partial') return 'refund_contract_partial';
    if (type === 'refund' || type === 'refund_full') return 'refund_contract';
    if (type === 'chargeback') return 'chgback_contract';
    if (type === 'balance_adjustment') return 'balance_adjustment';
  }
  return type;
}

export function buildSubmitPayload(params: {
  adjustmentType: AdjustmentType;
  paymentType: string;
  isContractPayment: boolean;
  adjustmentReason: string;
  autoSelectedData?: { adjusttype: string; adjustsubtype?: string };
  adjustmentSubtype: string;
  adjustmentAmount: string;
  calculation: PaymentCalculation;
  chargebackAmount: string;
  payment: PaymentAdjustmentHistoryLine | null;
  refundReason: string;
  note: string;
  isCovid: boolean;
  applySurcharge: boolean;
  chargeRefundFee: boolean;
  refundProcessingFee: boolean;
  isExternalRefund: boolean;
  externalPaymentSource: string;
  externalCheckNumber: string;
  chargebackStatus: string;
  caseNumber: string;
  txnNumber: string;
  caseDate: string;
  chargebackNotificationDate: string;
  responseDeadline: string;
  balanceAdjustmentStatus: string;
  balanceAdjustmentAction: string;
  adjustmentLeaseStatus: string;
}): PaymentAdjustmentSubmitPayload {
  const {
    adjustmentType,
    paymentType,
    isContractPayment,
    adjustmentReason,
    autoSelectedData,
    adjustmentSubtype,
    adjustmentAmount,
    calculation,
    chargebackAmount,
    payment,
    refundReason,
    note,
    isCovid,
    applySurcharge,
    chargeRefundFee,
    refundProcessingFee,
    isExternalRefund,
    externalPaymentSource,
    externalCheckNumber,
    chargebackStatus,
    caseNumber,
    txnNumber,
    caseDate,
    chargebackNotificationDate,
    responseDeadline,
    balanceAdjustmentStatus,
    balanceAdjustmentAction,
    adjustmentLeaseStatus,
  } = params;

  const mappedType = mapAdjustmentTypeToAngular(adjustmentType, paymentType);

  let resolvedAdjustmentReason = adjustmentReason;
  if (autoSelectedData?.adjusttype === 'increase') {
    resolvedAdjustmentReason = 'RENT_INCREASED';
  } else if (autoSelectedData?.adjusttype === 'decrease') {
    resolvedAdjustmentReason = 'RENT_DECREASED';
  } else if (autoSelectedData?.adjusttype === 'chgback_contract') {
    resolvedAdjustmentReason =
      autoSelectedData.adjustsubtype ?? resolvedAdjustmentReason;
  }

  const adjustmentAmountForRequest = amountString(
    adjustmentAmount || calculation.adjustmentPayment,
  );
  const chargebackAmountForRequest = positiveAmountString(
    chargebackAmount || payment?.payment_amount,
  );
  const paymentAmountForRequest = positiveAmountString(payment?.payment_amount);
  const refundAmountForRequest =
    adjustmentType === 'chargeback'
      ? isContractPayment
        ? adjustmentAmountForRequest
        : paymentAmountForRequest
      : adjustmentAmountForRequest;
  const angularAdjustmentData =
    adjustmentType === 'chargeback'
      ? {
          totalBalance: calculation.startBalance,
          startBalance: calculation.startBalance,
          adjustmentPayment: '0',
          balanaceOwnPayment: calculation.balanaceOwnPayment,
          refundAmount: 0,
          refundFee: 0,
        }
      : {
          totalBalance: calculation.totalBalance,
          startBalance: calculation.startBalance,
          adjustmentPayment: adjustmentAmountForRequest,
          balanaceOwnPayment: calculation.balanaceOwnPayment,
          refundAmount: calculation.refundAmount,
          refundFee: calculation.refundFee ?? 0,
        };

  return {
    adjustment_type: mappedType,
    adjustment_reason: resolvedAdjustmentReason,
    adjustment_subtype: adjustmentSubtype,
    refund_amount: refundAmountForRequest,
    reason: refundReason,
    notes: note,
    covid_19: adjustmentType === 'invoice' ? undefined : isCovid,
    apply_surcharge:
      adjustmentType === 'invoice' ? (applySurcharge ? '1' : '0') : undefined,
    charge_refund_fee: chargeRefundFee,
    refund_processing_fee: refundProcessingFee,
    is_external: isExternalRefund,
    payment_source: isExternalRefund
      ? externalPaymentSource
      : payment?.payment_source,
    check_number: externalCheckNumber,
    chargeback_status: chargebackStatus,
    case_number: caseNumber,
    txn_number: txnNumber || payment?.transaction_id || payment?.auth_code,
    case_date: caseDate || formatCaseDate(payment?.created_at),
    chargeback_notification_date: chargebackNotificationDate,
    response_deadline: responseDeadline,
    chargeback_amount: chargebackAmountForRequest,
    adjustment_data: angularAdjustmentData,
    balance_adjustment_status: balanceAdjustmentStatus,
    balance_adjustment_action: balanceAdjustmentAction,
    adjustment_lease_status: adjustmentLeaseStatus,
  };
}

export function validateAdjustmentBeforeConfirm(params: {
  requireNote: boolean;
  note: string;
  adjustmentType: AdjustmentType;
  adjustmentAmount: string;
  chargebackAmount: string;
  includeNote: boolean;
}) {
  const {
    requireNote,
    note,
    adjustmentType,
    adjustmentAmount,
    chargebackAmount,
    includeNote,
  } = params;
  if (requireNote && !note.trim() && includeNote) {
    return { valid: false, noteError: 'Note is required', amountError: '' };
  }
  if (adjustmentType === 'chargeback') {
    if (chargebackAmount.trim() === '') {
      return {
        valid: false,
        noteError: '',
        amountError: 'Chargeback amount is required',
      };
    }
    if (!isValidAmountInput(chargebackAmount)) {
      return {
        valid: false,
        noteError: '',
        amountError: 'Chargeback amount is invalid',
      };
    }
    return { valid: true, noteError: '', amountError: '' };
  }
  if (adjustmentType && adjustmentAmount.trim() === '') {
    return {
      valid: false,
      noteError: '',
      amountError: 'Adjustment amount is required',
    };
  }
  if (adjustmentType && !isValidAmountInput(adjustmentAmount)) {
    return {
      valid: false,
      noteError: '',
      amountError: 'Adjustment amount is invalid',
    };
  }
  return { valid: true, noteError: '', amountError: '' };
}

export function formatPaymentChargebackAmount(
  payment: PaymentAdjustmentHistoryLine | null,
) {
  return formatCurrencyInput(payment?.payment_amount ?? '', true);
}

export function formatDefaultAdjustmentAmount(
  adjustmentType: AdjustmentType,
  defaultAdjustmentAmount: number,
) {
  return adjustmentType ? formatCurrencyInput(defaultAdjustmentAmount) : '';
}
